import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Calculator, Clock, Zap, Shield, CheckCircle } from 'lucide-react';
import FilloutButton from '@/components/FilloutButton';

const FilloutPricingCalculator = () => {
  const { t } = useTranslation('fillout');
  const [formCount, setFormCount] = useState([3]);
  const [complexity, setComplexity] = useState(1); // 0: Simple, 1: Medium, 2: Complex
  const [hasPayments, setHasPayments] = useState(false);
  const [hasWhiteLabel, setHasWhiteLabel] = useState(false);
  const [isRush, setIsRush] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);

  const complexityOptionsData = t('calculator.complexityOptions', { returnObjects: true });
  const complexityOptions = Array.isArray(complexityOptionsData) ? 
    (complexityOptionsData as Array<{ label: string; description: string }>).map((option, index) => ({
      ...option,
      multiplier: [1, 1.6, 2.4][index]
    })) : [];

  useEffect(() => {
    const basePrice = 180; // Base price per form (higher for Fillout features)
    const forms = formCount[0];
    const complexityMultiplier = complexityOptions[complexity].multiplier;
    
    let price = forms * basePrice * complexityMultiplier;
    
    if (hasPayments) price += forms * 120; // Payment integration cost
    if (hasWhiteLabel) price += forms * 90; // White-label setup
    if (isRush) price *= 1.5; // Rush delivery
    
    // Volume discount
    if (forms >= 8) price *= 0.9; // 10% discount for 8+ forms
    if (forms >= 15) price *= 0.85; // 15% total discount for 15+ forms
    
    setTotalPrice(Math.round(price));
  }, [formCount, complexity, hasPayments, hasWhiteLabel, isRush]);

  const deliveryTime = () => {
    const baseDays = formCount[0] * (complexity + 1) * 2.5;
    return isRush ? Math.ceil(baseDays / 2) : baseDays;
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-primary/5 via-background to-accent/5">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-3 sm:space-y-4 mb-12 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold">
            {t('calculator.title')}
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
            {t('calculator.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 max-w-6xl mx-auto">
          {/* Calculator Form */}
          <Card className="relative overflow-hidden border-primary/20 shadow-xl order-2 lg:order-1">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5"></div>
            <CardHeader className="relative z-10 pb-4 sm:pb-6">
              <CardTitle className="flex items-center gap-3 text-lg sm:text-xl">
                <Calculator className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                {t('calculator.configure')}
              </CardTitle>
            </CardHeader>
            <CardContent className="relative z-10 space-y-6 sm:space-y-8 p-4 sm:p-6">
              {/* Form Count */}
              <div className="space-y-3 sm:space-y-4">
                <label className="text-sm font-medium">{t('calculator.formsLabel')} {formCount[0]}</label>
                <Slider
                  value={formCount}
                  onValueChange={setFormCount}
                  max={40}
                  min={1}
                  step={1}
                  className="w-full touch-manipulation"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>1 form</span>
                  <span>40+ forms</span>
                </div>
              </div>

              {/* Complexity */}
              <div className="space-y-3 sm:space-y-4">
                <label className="text-sm font-medium">{t('calculator.complexityLabel')}</label>
                <div className="grid gap-2 sm:gap-3">
                  {complexityOptions.map((option, index) => (
                    <Card 
                      key={index}
                      className={`cursor-pointer border-2 transition-all duration-300 ${
                        complexity === index 
                          ? 'border-primary bg-primary/5' 
                          : 'border-muted hover:border-primary/50'
                      }`}
                      onClick={() => setComplexity(index)}
                    >
                      <CardContent className="p-3 sm:p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-sm sm:text-base truncate">{option.label}</h4>
                            <p className="text-xs sm:text-sm text-muted-foreground leading-tight">{option.description}</p>
                          </div>
                          <Badge variant={complexity === index ? "default" : "secondary"} className="text-xs flex-shrink-0 ml-2">
                            {option.multiplier}x
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Add-ons */}
              <div className="space-y-3 sm:space-y-4">
                <label className="text-sm font-medium">{t('calculator.additionalServices')}</label>
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center justify-between p-3 sm:p-4 border rounded-lg">
                    <div className="flex-1 min-w-0 mr-3">
                      <h4 className="font-medium text-sm sm:text-base">{t('calculator.paymentProcessing')}</h4>
                      <p className="text-xs sm:text-sm text-muted-foreground">{t('calculator.paymentDescription')}</p>
                    </div>
                    <Switch checked={hasPayments} onCheckedChange={setHasPayments} className="flex-shrink-0" />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 sm:p-4 border rounded-lg">
                    <div className="flex-1 min-w-0 mr-3">
                      <h4 className="font-medium text-sm sm:text-base">{t('calculator.whiteLabel')}</h4>
                      <p className="text-xs sm:text-sm text-muted-foreground">{t('calculator.whiteLabelDescription')}</p>
                    </div>
                    <Switch checked={hasWhiteLabel} onCheckedChange={setHasWhiteLabel} className="flex-shrink-0" />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 sm:p-4 border rounded-lg">
                    <div className="flex-1 min-w-0 mr-3">
                      <h4 className="font-medium text-sm sm:text-base">{t('calculator.rushDelivery')}</h4>
                      <p className="text-xs sm:text-sm text-muted-foreground">{t('calculator.rushDescription')}</p>
                    </div>
                    <Switch checked={isRush} onCheckedChange={setIsRush} className="flex-shrink-0" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Price Display */}
          <div className="space-y-4 sm:space-y-6 order-1 lg:order-2">
            {/* Main Price Card */}
            <Card className="relative overflow-hidden border-primary/20 shadow-xl bg-gradient-to-br from-background to-primary/5">
              <CardHeader>
                <CardTitle className="text-center text-2xl">
                  {t('calculator.estimateTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center space-y-6">
                <div className="relative">
                  <div className="text-5xl font-bold text-primary animate-pulse">
                    ${totalPrice.toLocaleString()}
                  </div>
                  <div className="text-muted-foreground">{t('calculator.totalCost')}</div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 justify-center">
                    <Clock className="w-5 h-5 text-blue-500" />
                    <div>
                      <div className="font-semibold">{deliveryTime()} days</div>
                      <div className="text-xs text-muted-foreground">{t('calculator.deliveryTime')}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 justify-center">
                    <Zap className="w-5 h-5 text-yellow-500" />
                    <div>
                      <div className="font-semibold">{formCount[0]} forms</div>
                      <div className="text-xs text-muted-foreground">{t('calculator.totalForms')}</div>
                    </div>
                  </div>
                </div>

                {formCount[0] >= 8 && (
                  <Badge className="bg-green-500/10 text-green-700 border-green-200">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    {t('calculator.volumeDiscount')}
                  </Badge>
                )}

                <FilloutButton className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-6 text-lg font-semibold">
                  {t('calculator.getQuote')}
                </FilloutButton>
              </CardContent>
            </Card>

            {/* Features Included */}
            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shield className="w-5 h-5 text-primary" />
                  {t('calculator.whatsIncluded')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {(t('calculator.includedFeatures', { returnObjects: true }) as string[]).map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Trust Signals */}
            <div className="text-center space-y-3">
              <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  {t('calculator.noHiddenFees')}
                </div>
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  {t('calculator.fixedPrice')}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('calculator.disclaimer')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FilloutPricingCalculator;